import { AfterViewInit, Directive, HostListener, OnInit, inject, input } from '@angular/core';
import { diag } from '@opentelemetry/api';

import { TracingService } from './tracing.service';

/**
 * A directive that allows you to start a span onClick event with the provided tracingOperationName
 *
 * 'tracingOperationName' span operation name
 *
 * 'tracingSpanIdentifier' span Id. In case you have multiple start point, you are able to finish this span
 * using rwcUtilsTracingFinishSpan directive providing the same tracingSpanIdentifier.
 *
 * example:
 * the following code will start a span as soon as the user click the element
 * <element
 *  rwcUtilsTracingStartSpan
 *  tracingSpanIdentifier="search"
 *  tracingOperationName="contract search"
 * </element>
 */
@Directive({
    selector: '[rwcUtilsTracingStartSpan]'
})
export class RwcStartSpanDirective {
    #tracingService = inject(TracingService);

    tracingSpanIdentifier = input<string>();
    tracingOperationName = input.required<string>();

    @HostListener('click')
    protected clickEvent() {
        diag.debug(
            `start span on click event - opName: ${this.tracingOperationName}, tracingSpanIdentifier: ${this.tracingSpanIdentifier}`
        );
        this.#tracingService.startSpan(this.tracingOperationName(), {}, this.tracingSpanIdentifier());
    }
}

/**
 * A directive that allows you to finish a span with the provided tracingSpanIdentifier
 *
 * 'tracingSpanIdentifier' span id that ou want to finish
 *
 * 'tracingTags' object with tags to be added to the span
 *
 * examples:
 * the following code will finish the span started with the tracingSpanIdentifier "search"
 * as soon as the element view init
 *
 * <div  *ngIf="resultValue"
 *  rwcUtilsTracingFinishSpan
 *  tracingSpanIdentifier="search"
 *  [tracingTags]="{ key: 'value' }"
 * >
 *  <div>{{ resultValue }}</div>
 * </div>
 */
@Directive({
    selector: '[rwcUtilsTracingFinishSpan]'
})
export class RwcFinishSpanDirective implements OnInit, AfterViewInit {
    #tracingService = inject(TracingService);

    tracingSpanIdentifier = input<string>();
    tracingOperationName = input<string>();
    tracingTags = input<Record<string, any>>();

    ngOnInit(): void {
        if (!this.tracingSpanIdentifier() && !this.tracingOperationName()) {
            throw new Error(`At least one of attribute 'tracingSpanIdentifier' or 'tracingOperationName' are required`);
        }
    }

    ngAfterViewInit(): void {
        this.#finishSpan();
    }

    #finishSpan(): void {
        const operationName = this.tracingOperationName() || (this.tracingSpanIdentifier() as string);
        const span = this.#tracingService.getSpan(operationName);
        if (this.tracingTags() && span) {
            span.setAttributes(this.tracingTags() || {});
        }
        this.#tracingService.finishSpan(operationName);
    }
}
